import styles from "./Drawer.module.scss";
import * as Dialog from "@radix-ui/react-dialog";
import { PointerDownOutsideEvent } from "@radix-ui/react-dismissable-layer";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import * as React from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { IconClose } from "../icons";
import classNames from "classnames";
import { DialogContentProps, DialogProps } from "@radix-ui/react-dialog";
import { ButtonIcon } from "../buttons";

export type DrawerPosition = "right" | "bottom";

type D = DialogContentProps & DialogProps;

export interface DrawerProps extends D {
  /**
   * Whether to close the drawer when a pointer event occurs outside the bounds of the component.
   * @default true
   */
  closeOnOutsideClick?: boolean;

  /** Whether to close the drawer when the escape key is pressed.
   * @default true
   */
  closeOnEscapeKeyPress?: boolean;

  /** An accessible title to be announced when the dialog is opened.
   * The text is also displayed at the top of the drawer if the header has not been overriden */
  title?: string;

  /** An accessible description to be announced when the dialog is opened.
   * This description is not visible anywhere in the component, it is only announced by the screen reader.*/
  description?: string;

  /** The button that opens the dialog. */
  trigger?: React.ReactNode;

  /** Elements to render in the heading section of the drawer. This is only meant to be used as an escape hatch.
   * This will override the existing title and close button elements in the header.*/
  header?: React.ReactNode;

  /** Elements to render in the footer of the drawer,
   * this will always be visible at the very bottom of the drawer, regardless of body scrolling.
   * If footer is not provided, a section will not be rendered.*/
  footer?: React.ReactNode;

  /**
   * Width of the drawer in pixels, optional.
   * @default 312*/
  width?: number;

  /**A callback event that is emitted whenever the drawer is closed by the user.
   * This can include clicking outside the drawer (if enabled),
   * pressing the escape key (if enabled),
   * or pressing the close button.
   */
  onUserClose?: () => void;

  position?: DrawerPosition;
}

/**
 * A primitive component that creates a drawer on the right hand side of the screen.
 * The drawer consists of three sections: header, body, and footer.
 * Each can be supplied children - Header and footer will remain static at the start and end of the drawer.
 * If the children in the body overflow, the body can then be scrolled.
 */
export const Drawer = (props: DrawerProps) => {
  const {
    closeOnOutsideClick = true,
    closeOnEscapeKeyPress = true,
    title,
    description,
    trigger,
    children,
    footer,
    header,
    width = 312,
    onUserClose,
    position = "right",
    open,
    defaultOpen,
    onOpenChange,
    modal,
    ...rest
  } = props;

  const handlePointerDownOutside = (event: PointerDownOutsideEvent): void => {
    if (!closeOnOutsideClick) {
      event.preventDefault();
      return;
    }

    if (onUserClose) onUserClose();
  };

  const handleEscapeKeyDown = (event: KeyboardEvent): void => {
    if (!closeOnEscapeKeyPress) {
      event.preventDefault();
      return;
    }

    if (onUserClose) onUserClose();
  };

  const titleClasses = classNames(styles.dialogHeader, styles.dialogTitle, {
    [styles.dialogTitleLeft]: position === "bottom",
  });

  const Header = header ? (
    <div className={styles.dialogHeader}>{header}</div>
  ) : (
    <Dialog.Title className={titleClasses}>
      <span className={styles.dialogTitleText}>{title}</span>

      <Dialog.Close asChild>
        <div className={styles.dialogTitleClose}>
          <ButtonIcon variant="tertiary-black" onClick={onUserClose}>
            <IconClose className={styles.closeIcon} />
          </ButtonIcon>
        </div>
      </Dialog.Close>
    </Dialog.Title>
  );

  const Footer = (
    <>{footer && <div className={styles.dialogFooter}>{footer}</div>}</>
  );

  /* Workaround to the following issue: https://github.com/radix-ui/primitives/issues/1662
    Radix UI appears to use a third party library that sets the body position to relative whenever the dialog is open.
    Component works fine without this modification in Storybooks, but the entire app disappears whenever the 
    dialog is rendered in MyAdmin.
    */
  document.body.setAttribute("style", "position:static !important");

  const contentClasses = classNames(styles.dialogContent, {
    [styles.dialogContentRight]: position === "right",
    [styles.animateInRight]: position === "right",
    [styles.dialogContentBottom]: position === "bottom",
    [styles.animateInBottom]: position === "bottom",
  });

  return (
    <Dialog.Root
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={onOpenChange}
      modal={modal}
    >
      {/*The element that opens the drawer*/}
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>

      <Dialog.Portal>
        {/*Overlays the entire screen, this is what darkens everything but the drawer*/}
        <Dialog.Overlay className={styles.dialogOverlay} />

        {/*Main body of the drawer*/}
        <Dialog.Content
          className={contentClasses}
          style={{ width: position === "right" ? width : undefined }}
          onPointerDownOutside={handlePointerDownOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
          {...rest}
        >
          <VisuallyHidden>
            <Dialog.Description>{description}</Dialog.Description>
          </VisuallyHidden>

          <div className={styles.dialogMain}>
            <div className={styles.dialogBody}>
              <ScrollArea.Root className={styles.scrollAreaRoot}>
                <ScrollArea.Viewport className={styles.scrollAreaViewport}>
                  {children}
                </ScrollArea.Viewport>
                <ScrollArea.Scrollbar
                  className={styles.scrollAreaScrollbar}
                  orientation="vertical"
                >
                  <ScrollArea.Thumb className={styles.scrollAreaThumb} />
                </ScrollArea.Scrollbar>
                {position !== "bottom" && (
                  <ScrollArea.Scrollbar
                    className={styles.scrollAreaScrollbar}
                    orientation="horizontal"
                  >
                    <ScrollArea.Thumb className={styles.scrollAreaThumb} />
                  </ScrollArea.Scrollbar>
                )}
                <ScrollArea.Corner className={styles.scrollAreaCorner} />
              </ScrollArea.Root>
            </div>
            {Footer}
          </div>

          {/*
            Header is put as the very last element in order to not autofocus the close button (We want to focus on the first child instead).
            This also means it will be the last element to be focused on when tabbing.
            */}

          {Header}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
